import { FirebaseOptions } from 'firebase/app';

/** Backend payload shape: `newDatabaseURL` opts this setup into the auth-enforced RTDB instance. */
export type BackendFirebaseConfig = FirebaseOptions & { newDatabaseURL?: string };

/** localStorage key for the Firebase config of the last authenticated session; public by design, so plain JSON. */
export const FIREBASE_CONFIG_CACHE_KEY = 'fb_config';

/** localStorage flag that the backend has App Check on, so an app restart can restart it without the session call. */
export const FIREBASE_APP_CHECK_FLAG = 'fb_app_check';

/** Cached entry; `api` scopes it to the backend that issued it so a domain switch never reuses another tenant's project. */
interface CachedFirebaseConfig {
  api: string;
  config: BackendFirebaseConfig;
}

let firebaseConfig: FirebaseOptions | null = null;

/** Stores the Firebase config fetched at boot, preferring `newDatabaseURL` so RTDB migration is a per-backend switch. */
export function setFirebaseConfig(config: BackendFirebaseConfig): void {
  const { newDatabaseURL, ...options } = config;
  firebaseConfig = { ...options, databaseURL: newDatabaseURL || options.databaseURL };
}

/**
 * Returns the stored Firebase config.
 * @returns The Firebase config fetched at boot.
 * @throws {Error} If called before a successful fetch has stored a config — callers must check `hasFirebaseConfig()` first.
 */
export function getFirebaseConfig(): FirebaseOptions {
  if (!firebaseConfig) {
    throw new Error('Firebase configuration has not been initialised.');
  }
  return firebaseConfig;
}

/** Whether a Firebase config has been fetched and stored for this session. */
export function hasFirebaseConfig(): boolean {
  return firebaseConfig !== null;
}

/** True for a config the SDK can initialise with; a tenant that has moved off prelogin sends `{}`. */
export function isUsableFirebaseConfig(config: BackendFirebaseConfig | null | undefined): config is BackendFirebaseConfig {
  return !!config?.apiKey;
}

/**
 * Caches the session's Firebase config so the next boot can initialise Firebase without prelogin.
 * @param api - Backend base URL that issued the config
 * @param config - Config from `auth/firebase/session/`
 */
export function cacheFirebaseConfig(api: string, config: BackendFirebaseConfig): void {
  try {
    window.localStorage.setItem(FIREBASE_CONFIG_CACHE_KEY, JSON.stringify({ api, config }));
  } catch {
    // Swallowed by design: without the cache the next boot falls back to prelogin's config.
  }
}

/**
 * Records whether the backend has App Check on; a blocked storage is ignored.
 * @param enabled - Whether the session carried an App Check token
 */
export function cacheAppCheckEnabled(enabled: boolean): void {
  try {
    if (enabled) {
      window.localStorage.setItem(FIREBASE_APP_CHECK_FLAG, '1');
    } else {
      window.localStorage.removeItem(FIREBASE_APP_CHECK_FLAG);
    }
  } catch {
    // Swallowed by design: without the flag a restart just skips App Check until the next session call.
  }
}

/** Whether the last session had App Check on. */
export function isAppCheckCached(): boolean {
  try {
    return window.localStorage.getItem(FIREBASE_APP_CHECK_FLAG) === '1';
  } catch {
    return false;
  }
}

/**
 * Reads the cached Firebase config for this backend.
 * @param api - Backend base URL the app is booting against
 * @returns The cached config, or null when absent, unreadable or issued by another backend
 */
export function loadCachedFirebaseConfig(api: string): BackendFirebaseConfig | null {
  try {
    const cached = JSON.parse(window.localStorage.getItem(FIREBASE_CONFIG_CACHE_KEY) ?? 'null') as CachedFirebaseConfig | null;
    return cached?.api === api && isUsableFirebaseConfig(cached.config) ? cached.config : null;
  } catch {
    return null;
  }
}

/**
 * Applies the config cached by the last login's session call for this backend.
 * @param api - Backend base URL the app is booting against
 * @returns Whether a config was stored
 */
export function applyBootFirebaseConfig(api: string): boolean {
  const config = loadCachedFirebaseConfig(api);
  if (!config) {
    return false;
  }
  setFirebaseConfig(config);
  return true;
}

/** Outcome of the backend Google redirect, read off the URL before the router can drop it. */
export interface GoogleRedirectResult {
  code: string;
  error: string;
}

let googleRedirectResult: GoogleRedirectResult | null = null;

/**
 * Takes `google_code` / `google_error` off the current URL and strips them, so a refresh never replays the code.
 * Runs before Angular boots: the signin redirect in AppComponent navigates without the query string.
 */
export function captureGoogleRedirectResult(): void {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('google_code') ?? '';
  const error = url.searchParams.get('google_error') ?? '';
  if (!code && !error) {
    return;
  }
  googleRedirectResult = { code, error };
  url.searchParams.delete('google_code');
  url.searchParams.delete('google_error');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/** Returns the captured Google redirect result once, then forgets it. */
export function takeGoogleRedirectResult(): GoogleRedirectResult | null {
  const result = googleRedirectResult;
  googleRedirectResult = null;
  return result;
}
